import { useState, type CSSProperties, type KeyboardEvent } from 'react';

export interface AmountInputProps {
  value: string; // raw numeric string, e.g. '1374415' or '12.5'
  onChange: (raw: string) => void;
  disabled?: boolean;
  style?: CSSProperties;
  ariaLabel?: string;
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
  // Roving-tabindex support: place the `data-cell` marker and tabIndex on the
  // real focusable <input> so a parent grid can find/move focus to it.
  dataCell?: boolean;
  tabIndex?: number;
}

// Keep only digits and a single decimal point.
function clean(s: string): string {
  let v = s.replace(/[^\d.]/g, '');
  const i = v.indexOf('.');
  if (i >= 0) v = v.slice(0, i + 1) + v.slice(i + 1).replace(/\./g, '');
  return v;
}

// Comma-group the integer part; show '' for empty/zero so the grid stays clean.
function withCommas(raw: string): string {
  if (!raw) return '';
  const n = Number(raw);
  if (!isFinite(n) || n === 0) return '';
  const parts = raw.split('.');
  const int = parts[0] ?? '';
  const dec = parts[1];
  const intFmt = Number(int || '0').toLocaleString('en-US');
  // Drop an empty trailing-dot decimal so a blurred value never shows "12."
  return dec ? `${intFmt}.${dec.slice(0, 2)}` : intFmt;
}

export function AmountInput({
  value,
  onChange,
  disabled,
  style,
  ariaLabel,
  onKeyDown,
  dataCell,
  tabIndex,
}: AmountInputProps) {
  const [focused, setFocused] = useState(false);
  const display = focused ? (value === '0' ? '' : value) : withCommas(value);
  return (
    <input
      className="input tnum"
      inputMode="decimal"
      aria-label={ariaLabel}
      disabled={disabled}
      data-cell={dataCell ? '' : undefined}
      tabIndex={tabIndex}
      value={display}
      onFocus={() => setFocused(true)}
      onBlur={() => {
        setFocused(false);
        // Normalize empty or non-numeric (e.g. a lone '.') to '0' so balance
        // sums never see NaN.
        if (value === '' || !isFinite(Number(value))) onChange('0');
      }}
      onChange={(e) => onChange(clean(e.target.value) || '0')}
      onKeyDown={onKeyDown}
      style={{ width: '100%', height: 32, fontSize: 13, textAlign: 'right', ...style }}
    />
  );
}
